// A small round portrait of an agent drawn in SVG with the same look as its 3D figure (shirt, skin,
// hair colours and hair style from web/scene/layout.js lookFor). Colours go through CSS custom
// properties (no style attribute, CSP-safe). Used by the agent drawer, the task drawer and the
// approvals queue.
import { h, svg, statusDot, classNames } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { lookFor } from '../../scene/layout.js';
import './strings.js';

const HAIR = {
  short: [['path', { d: 'M12.6 17.2a7.4 7.4 0 0114.8 0c-1.9-2.6-4.4-3.7-7.4-3.7s-5.5 1.1-7.4 3.7z' }]],
  long: [['path', { d: 'M12.4 17.5a7.6 7.6 0 0115.2 0v8.6h-2.5v-7.4c-1.6-2.3-3.3-3.3-5.1-3.3s-3.5 1-5.1 3.3v7.4h-2.5z' }]],
  bun: [
    ['circle', { cx: 20, cy: 8.6, r: 3.3 }],
    ['path', { d: 'M12.6 17.2a7.4 7.4 0 0114.8 0c-1.9-2.6-4.4-3.7-7.4-3.7s-5.5 1.1-7.4 3.7z' }],
  ],
  curly: [
    ['circle', { cx: 14.2, cy: 13.6, r: 3.2 }], ['circle', { cx: 17.6, cy: 10.8, r: 3.3 }],
    ['circle', { cx: 22.4, cy: 10.8, r: 3.3 }], ['circle', { cx: 25.8, cy: 13.6, r: 3.2 }],
  ],
  cap: [
    ['path', { d: 'M12.5 16.4a7.5 7.5 0 0115 0z' }],
    ['path', { class: 'av-accent', d: 'M20 15.2h10.2a1 1 0 010 2H20z' }],
  ],
};

/**
 * The portrait of an agent.
 * @param {object} agent {id, name, look?}
 * @param {{size?: number, status?: string|null, label?: boolean, class?: string}} [opts]
 *   status: adds a live status dot; label: exposes the avatar to screen readers (else decorative)
 * @returns {HTMLElement}
 */
export function agentAvatar(agent, opts = {}) {
  const look = lookFor(agent || {});
  const size = opts.size || 40;
  const hair = (HAIR[look.hairStyle] || HAIR.short).map(([tag, attrs]) => svg(tag, { class: 'av-hair', ...attrs }));
  const pic = svg('svg', { class: 'av-svg', viewBox: '0 0 40 40', width: size, height: size, focusable: 'false', 'aria-hidden': 'true' },
    svg('circle', { class: 'av-bg', cx: 20, cy: 20, r: 20 }),
    svg('path', { class: 'av-body', d: 'M6.5 41c0-8.6 6-13.8 13.5-13.8S33.5 32.4 33.5 41z' }),
    svg('path', { class: 'av-collar', d: 'M16.4 27.8l3.6 4 3.6-4' }),
    svg('rect', { class: 'av-neck', x: 17.6, y: 22.5, width: 4.8, height: 5.4, rx: 2 }),
    svg('circle', { class: 'av-head', cx: 20, cy: 18, r: 7.2 }),
    ...hair);
  const labelled = !!opts.label;
  return h('span', {
    class: classNames(['ad-avatar', opts.class, { 'has-status': !!opts.status }]),
    style: { '--av-shirt': look.shirt, '--av-skin': look.skin, '--av-hair': look.hair, '--av-accent': look.accent, '--av-size': `${size}px` },
    role: labelled ? 'img' : null,
    'aria-label': labelled ? t('ad.avatarLabel', { name: agent?.name || '' }) : null,
    'aria-hidden': labelled ? null : 'true',
  }, pic, opts.status ? h('span', { class: 'ad-avatar-status' }, statusDot(opts.status)) : null);
}
